import React from 'react';
import { CardStatsProps } from '@/types/components';

export const CardStats: React.FC<CardStatsProps> = ({
  title,
  value,
  icon,
  subtitle,
  trend,
  color = 'default',
  size = 'medium',
  loading = false,
  onClick,
  className = '',
}) => {
  const baseClasses =
    'bg-white rounded-lg border border-gray-200 transition-all duration-200';

  const interactiveClasses = onClick
    ? 'cursor-pointer hover:shadow-md hover:border-gray-300'
    : '';

  const sizeClasses = {
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8',
  };

  // Color variants for icons
  const colorClasses = {
    default: 'text-gray-400',
    blue: 'text-blue-500',
    green: 'text-green-500',
    yellow: 'text-yellow-500',
    red: 'text-red-500',
    purple: 'text-primary',
  };

  // Icon sizes based on card size
  const iconSizes = {
    small: 'text-[20px]',
    medium: 'text-[24px]',
    large: 'text-[28px]',
  };

  // Value text sizes
  const valueSizes = {
    small: 'text-xl',
    medium: 'text-2xl',
    large: 'text-3xl',
  };

  if (loading) {
    return (
      <div
        className={`${baseClasses} ${sizeClasses[size]} ${className}`.trim()}
      >
        <div className="animate-pulse">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
            </div>
            <div className="ml-4 flex-1">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${baseClasses} ${sizeClasses[size]} ${interactiveClasses} ${className}`.trim()}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center flex-1">
          {icon && (
            <div className="flex-shrink-0">
              <i
                className={`${icon} ${iconSizes[size]} ${colorClasses[color]}`}
              ></i>
            </div>
          )}
          <div className={icon ? 'ml-4' : ''}>
            <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
            <p className={`font-semibold text-gray-900 ${valueSizes[size]}`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
            )}
          </div>
        </div>

        {trend && (
          <div className="flex items-center text-sm">
            <i
              className={`${
                trend.direction === 'up'
                  ? 'ri-arrow-up-line'
                  : 'ri-arrow-down-line'
              } text-[16px] mr-1 ${
                trend.direction === 'up' ? 'text-green-500' : 'text-red-500'
              }`}
            ></i>
            <span
              className={`font-medium ${
                trend.direction === 'up' ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {Math.abs(trend.value)}%
            </span>
            {trend.period && (
              <span className="text-gray-500 ml-1">{trend.period}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardStats;
